import { useEffect, useRef } from 'react';
import axios, { type AxiosError } from 'axios';
import store, { useAppSelector } from '@/store';
import { authActions, authSelector } from '@/store/reducers/auth';
import axiosInstance from '@/utils/api-new';
import { triggerForcedSessionLogout } from '@/utils/forcedSessionLogout';
import { refreshTokens } from '@/services/tokenExchange';
import commonUtils from '@/utils/common';
import config from '@/config';
import { BaseResponse } from '@/types/response';
import { Progress, UserState } from '@/types/user';

const hasProgressChanged = (previous?: Progress, next?: Progress) => {
  if (!previous || !next) return previous !== next;

  return (
    previous.coins !== next.coins ||
    previous.achievedStars !== next.achievedStars ||
    previous.completedActivities !== next.completedActivities ||
    previous.completedUnits !== next.completedUnits ||
    previous.completedTopics !== next.completedTopics ||
    previous.userLevel !== next.userLevel
  );
};

const HEARTBEAT_INTERVAL_MS = 30_000;

/**
 * Polls the backend every 30 s while the user is logged in and the page is visible.
 *
 * When Cognito revokes a user's tokens (e.g. deal expiry via GlobalSignOut/AdminUserGlobalSignOut),
 * the next authenticated request returns 401. Two paths lead to the expired page:
 *
 * 1. Generic 401 (revoked/invalid token) — handled automatically by the response
 *    interceptor in api-new.ts: isForcedSessionAuthError → triggerForcedSessionLogout().
 *
 * 2. Deal-expired 401/403 (backend business-logic message) — isForcedSessionAuthError
 *    intentionally excludes these (reserved for the Redux DealHasExpired path that
 *    only runs inside userThunks.userInfo). The heartbeat handles this case explicitly.
 *
 * Each successful poll also carries the user's current progress (coins, stars,
 * completed activities/units/topics), so it doubles as the mechanism that keeps an
 * already-open page in sync when activities are completed elsewhere. The progress is
 * only dispatched into Redux when it actually differs from what's stored, to avoid
 * redundant re-renders every tick.
 */
const UseSessionHeartbeat = () => {
  const { loggedIn, userId } = useAppSelector(authSelector);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const stopHeartbeat = () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    if (!loggedIn || !userId) {
      stopHeartbeat();
      return stopHeartbeat;
    }

    const ping = async () => {
      if (document.hidden || !localStorage.getItem('token')) return;
      try {
        const response = await axiosInstance.get<BaseResponse<UserState>>(
          `${config.apiVersion}/user-data/${userId}?isLogin=false`
        );
        const nextProgress = response.data?.data?.progress;
        const currentProgress = store.getState().auth.user?.progress;

        if (nextProgress && hasProgressChanged(currentProgress, nextProgress)) {
          store.dispatch(authActions.setProgress(nextProgress));
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const ae = error as AxiosError;
          const status = ae.response?.status;
          const message = (
            ((ae.response?.data as Record<string, unknown>)?.message as string) ?? ''
          ).toLowerCase();
          const isDealExpired =
            message.includes('this deal has expired') || message.includes('user deal has expired');

          if ((status === 401 || status === 403) && isDealExpired) {
            triggerForcedSessionLogout();
          }
          // Generic 401 (revoked token) is caught by the response interceptor in
          // api-new.ts → isForcedSessionAuthError → triggerForcedSessionLogout().
        }
        // Network errors and 5xx responses are silently ignored — transient failures
        // should not kick the user out. The next heartbeat or real user action will retry.
      }
    };

    intervalRef.current = setInterval(ping, HEARTBEAT_INTERVAL_MS);

    return stopHeartbeat;
  }, [loggedIn, userId]);

  useEffect(() => {
    if (!loggedIn) return;

    const maybeRefreshOnWake = () => {
      if (document.visibilityState !== 'visible') return;
      const token = localStorage.getItem('token');
      if (!token) return;
      if (commonUtils.shouldTokenRefresh(token)) {
        void refreshTokens(store.dispatch);
      }
    };

    document.addEventListener('visibilitychange', maybeRefreshOnWake);
    window.addEventListener('focus', maybeRefreshOnWake);

    return () => {
      document.removeEventListener('visibilitychange', maybeRefreshOnWake);
      window.removeEventListener('focus', maybeRefreshOnWake);
    };
  }, [loggedIn]);
};

export default UseSessionHeartbeat;
